// prediction/wizards/wizardUtils.js

export const MAX_NUMBER = 200000;

/**
 * 数値入力のバリデーション
 */
export const validateNumericInput = (value, fieldName, isRequired = false, maxValue = null) => {
  if (!value || value.trim() === '') {
    return isRequired ? `${fieldName}は必須です` : null;
  }
  
  if (!/^\d+$/.test(value)) {
    return `${fieldName}は数字のみ入力できます`;
  }
  
  const numValue = parseInt(value, 10);
  if (isNaN(numValue) || numValue < 0) {
    return `${fieldName}は0以上の値を入力してください`;
  }
  
  if (numValue > MAX_NUMBER) {
    return `${fieldName}は${MAX_NUMBER.toLocaleString()}L以下で入力してください`;
  }
  
  if (maxValue && numValue > maxValue) {
    return `${fieldName}は${maxValue}L以下で入力してください`;
  }
  
  return null;
};

/**
 * 日付のバリデーション
 */
export const validateDate = (dateString) => {
  if (!dateString) return null;
  
  const today = new Date().toISOString().split('T')[0];
  if (dateString > today) {
    return '未来の日付は指定できません';
  }
  
  return null;
};

/**
 * ステップ別バリデーション
 */
export const validateStep = (stepId, formData, tankChoice, selectedProduct, showTankSetupStep) => {
  const errors = {};
  
  if (stepId === 'tank_setup') {
    if (tankChoice === null) {
      errors.tankSetup = 'タンク設置状況を選択してください';
    }
  }
  
  if (stepId === 'capacity') {
    const error = validateNumericInput(formData.totalCapacity, 'タンク容量', true);
    if (error) errors.totalCapacity = error;
  }
  
  if (stepId === 'consumption') {
    const hasTank = showTankSetupStep ? tankChoice : Boolean(selectedProduct?.tankInstalled);
    const isRequired = !hasTank; // タンクなしの場合は必須
    const error = validateNumericInput(formData.initialConsumptionRate, '月間消費量', isRequired);
    if (error) errors.initialConsumptionRate = error;
  }
  
  if (stepId === 'remaining') {
    const tankCapacity = formData.totalCapacity ? parseInt(formData.totalCapacity, 10) : null;
    const remainingError = validateNumericInput(
      formData.actualRemaining, 
      '現在の残量', 
      false, 
      tankCapacity
    );
    if (remainingError) errors.actualRemaining = remainingError;
    
    const dateError = validateDate(formData.measuredDate);
    if (dateError) errors.measuredDate = dateError;
  }
  
  return errors;
};

/**
 * 完了時の全体バリデーション
 */
export const validateComplete = (formData, tankChoice, showTankSetupStep, selectedProduct) => {
  const hasTank = showTankSetupStep ? tankChoice : Boolean(selectedProduct?.tankInstalled);
  
  // 必須データチェック
  if (hasTank && !formData.totalCapacity) {
    return 'タンク容量は必須です';
  }
  
  if (!hasTank && !formData.initialConsumptionRate) {
    return 'タンク未設置の場合、消費量の設定は必須です';
  }

  // 容量超過チェック
  if (hasTank && formData.actualRemaining && formData.totalCapacity) {
    const remaining = parseInt(formData.actualRemaining, 10);
    const capacity = parseInt(formData.totalCapacity, 10);
    if (remaining > capacity) {
      return `実測残量（${remaining}L）がタンク容量（${capacity}L）を超えています。修正してください。`;
    }
  }

  // 数値範囲チェック
  if (formData.totalCapacity && parseInt(formData.totalCapacity, 10) > MAX_NUMBER) {
    return `タンク容量は${MAX_NUMBER.toLocaleString()}L以下で入力してください`;
  }
  if (formData.initialConsumptionRate && parseInt(formData.initialConsumptionRate, 10) > MAX_NUMBER) {
    return `消費量は${MAX_NUMBER.toLocaleString()}L以下で入力してください`;
  }
  if (formData.actualRemaining && parseInt(formData.actualRemaining, 10) > MAX_NUMBER) {
    return `実測残量は${MAX_NUMBER.toLocaleString()}L以下で入力してください`;
  }

  return null; // エラーなし
};

/**
 * 🚀 修正：動的ステップ生成 - タンクなしでも残量ステップを追加
 */
export const generateSteps = (showTankSetupStep, tankSetupChoice, selectedProduct) => {
  const baseSteps = [];
  
  // タンク設置確認ステップ（新規顧客用）
  if (showTankSetupStep) {
    baseSteps.push({
      id: 'tank_setup',
      title: 'タンク設置確認',
      required: true
    });
  }
  
  // タンク設置状況に応じたステップ
  const hasTank = showTankSetupStep ? tankSetupChoice : Boolean(selectedProduct?.tankInstalled);
  
  if (hasTank) {
    // タンクありの場合：容量→消費量→残量
    baseSteps.push(
      {
        id: 'capacity',
        title: 'タンク容量',
        required: true
      },
      {
        id: 'consumption', 
        title: '月間消費量',
        required: false
      },
      {
        id: 'remaining',
        title: '現在の残量',
        required: false
      }
    );
  } else {
    // 🚀 修正：タンクなしの場合も消費量→残量の2ステップにする
    baseSteps.push(
      {
        id: 'consumption',
        title: '月間消費量',
        required: true // タンクなしの場合は必須
      },
      {
        id: 'remaining',
        title: '現在の残量',
        required: false // タンクなしでも残量入力は任意
      }
    );
  }
  
  return baseSteps;
};

/**
 * 次へボタン有効性判定
 */
export const canProceedToNext = (currentStep, steps, tankSetupChoice, formData, errors, showTankSetupStep, selectedProduct) => {
  const step = steps[currentStep];
  
  if (step.id === 'tank_setup') {
    return tankSetupChoice !== null;
  }
  
  if (step.id === 'capacity') {
    return formData.totalCapacity && !errors.totalCapacity;
  }
  
  if (step.id === 'consumption') {
    const hasTank = showTankSetupStep ? tankSetupChoice : Boolean(selectedProduct?.tankInstalled);
    if (!hasTank) {
      // タンクなしの場合は必須
      return formData.initialConsumptionRate && !errors.initialConsumptionRate;
    }
    return true; // タンクありの場合は任意
  }
  
  return true;
};

/**
 * 完了ボタン無効化判定
 */
export const isCompleteDisabled = (errors, showTankSetupStep, tankSetupChoice, selectedProduct, formData) => {
  // エラーがある場合は無効
  if (Object.keys(errors).length > 0) return true;
  
  const hasTank = showTankSetupStep ? tankSetupChoice : Boolean(selectedProduct?.tankInstalled);
  
  // タンクありの場合：容量は必須
  if (hasTank && !formData.totalCapacity) return true;
  
  // タンクなしの場合：消費量は必須
  if (!hasTank && !formData.initialConsumptionRate) return true;
  
  // 容量超過チェック
  if (hasTank && formData.actualRemaining && formData.totalCapacity) {
    const remaining = parseInt(formData.actualRemaining, 10);
    const capacity = parseInt(formData.totalCapacity, 10);
    if (!isNaN(remaining) && !isNaN(capacity) && remaining > capacity) return true;
  }
  
  return false;
};

/**
 * スキップボタン表示制御
 */
export const canSkipCurrentStep = (currentStep, steps, showTankSetupStep, tankSetupChoice, selectedProduct) => {
  const step = steps[currentStep];
  
  // タンク設置確認は必須
  if (step.id === 'tank_setup') return false;
  
  // タンクなし時の消費量は必須
  if (step.id === 'consumption') {
    const hasTank = showTankSetupStep ? tankSetupChoice : Boolean(selectedProduct?.tankInstalled);
    return hasTank; // タンクありの場合のみスキップ可能
  }
  
  // その他のステップは基本的にスキップ可能
  return !step.required;
};